/* eslint-disable react/style-prop-object */
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import Box from 'ui-box';
import { CompanyThumbnail } from '../companies';
import { ContactThumbnail } from '../contacts';
import {
  Button,
  Container,
  defaultColors,
  Heading,
  KeyValue,
  majorScale,
  Menu,
  MenuDivider,
  MenuItem,
  minorScale,
  PageContainer,
  Popover,
  Section,
  Tag,
  PageHeader,
  PageLoader,
  defaultFills,
  Icon,
  useToast,
  useAlert,
} from '../../npaper';
import { SaleDetailDialog } from './SaleDetailsDialog';
import { SaleThumbnail } from './SaleThumbnail';
import {
  formatDeliveryType,
  ICompany,
  ISale,
  PaymentTermsType,
  SaleStatus,
  SaleType,
  useStores,
} from '../../stores';
import { Spinner } from '../../npaper/Spinner';
import { SaleStatusTag } from './SaleStatusTag';
import { SaleItemsTable } from './SaleItemsTable';
import { saleTypeTitle, SaleTypeTitle } from './SaleTypeTitle';
import { UserThumbnail } from '../settings/UserThumbnail';
import { CreateOrderDialog } from './CreateOrderDialog';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { CreditNotesSection } from './CreditNotesSection';
import { SendItemSideSheet } from '../../npaper/SendItemSideSheet';
import { FormattedTerms } from '../../npaper/FormattedTerms';
import { TimeLine } from '../time-line/TimeLine';

export const SalePage = observer(() => {
  const match = useRouteMatch<{ id: string }>();
  const id = match.params.id;

  const [sendSale, setSendSale] = useState(false);
  const [showCreateOrder, setShowCreateOrder] = useState(false);

  const [sale, setSale] = useState<ISale>();
  const [notFound, setNotFound] = useState(false);

  const { saleStore, authStore } = useStores();

  const toaster = useToast();
  const intl = useIntl();
  const history = useHistory();

  const deleteAlert = useAlert({
    title: intl.formatMessage({
      defaultMessage: 'Deleting sale',
      description: 'Delete sale alert title',
    }),
    message: intl.formatMessage({
      defaultMessage:
        'Are you sure you want to delete the sale? This cannot be undone.',
      description: 'Delete sale alert message',
    }),
    confirmButtonText: intl.formatMessage({
      defaultMessage: 'Delete',
      description: 'Delete sale alert confirm button',
    }),
    intent: 'danger',
    icon: 'warning-sign',
  });

  const createInvoiceAlert = useAlert({
    title: intl.formatMessage({
      defaultMessage: 'Create invoice',
      description: 'Create sale alert title',
    }),
    message: intl.formatMessage({
      defaultMessage:
        'You are about to create a draft invoice from this order. You cannot go back after doing so.',
      description: 'Create sale alert message',
    }),
    intent: 'success',
    icon: 'dollar',
  });

  const confirmInvoiceSuccessAlert = useAlert({
    title: intl.formatMessage({
      defaultMessage: 'Send invoice to customer',
      description: 'Invoice confirmed alert title',
    }),
    message: intl.formatMessage({
      defaultMessage:
        'Invoice has been confirmed successfully. Do you want to send the invoice to the customer now?',
      description: 'Invoice confirmed alert message',
    }),
    intent: 'info',
    icon: 'send-message',
    confirmButtonText: intl.formatMessage({
      defaultMessage: 'Send invoice',
      description: 'Invoice confirmed alert confirm button',
    }),
    cancelButtonText: intl.formatMessage({
      defaultMessage: 'Not now',
      description: 'Invoice confirmed alert cancel button',
    }),
  });

  useEffect(() => {
    let isSub = true;
    setSale(undefined);
    saleStore
      .getItem(id, true)
      .then((sale) => {
        isSub && setSale(sale);
      })
      .catch(() => setNotFound(true));
    return () => {
      isSub = false;
    };
  }, [saleStore, id]);

  const [showDetails, setShowDetails] = useState(false);

  const createOrder = async () => {
    if (!sale) return;
    try {
      await saleStore.changeState(sale, SaleType.ORDER, SaleStatus.OPEN);
      toaster.addToast({
        message: intl.formatMessage({
          defaultMessage: 'Order was successfully created',
        }),
        intent: 'success',
      });
    } catch (err) {
      // TODO: Maybe we need more detailed error handling
      toaster.showDefaultError(err);
      return;
    }
  };

  const orderConfirmationMessage = intl.formatMessage({
    defaultMessage: 'order confirmation',
  });

  const sendSaleMessage = intl.formatMessage(
    {
      defaultMessage: 'Send {saleTitle}',
    },
    {
      saleTitle:
        sale && sale.type === SaleType.ORDER
          ? orderConfirmationMessage
          : saleTypeTitle(intl, sale ? sale.type : SaleType.OFFER),
    }
  );

  const downloadSaleMessage = intl.formatMessage(
    {
      defaultMessage: 'Download {saleTitle}',
    },
    {
      saleTitle: saleTypeTitle(intl, sale ? sale.type : SaleType.OFFER),
    }
  );

  return !sale ? (
    <PageContainer>
      {notFound ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <Icon
            icon="error"
            size={40}
            marginBottom={minorScale(2)}
            color={defaultFills.danger[500]}
          />
          <Heading size={400}>
            <FormattedMessage defaultMessage="Sale could not be found" />
          </Heading>
          <p className="muted font-mono">ID {id}</p>
        </Box>
      ) : (
        <PageLoader />
      )}
    </PageContainer>
  ) : (
    <PageContainer position="relative">
      <SaleDetailDialog
        isShown={showDetails}
        onClose={() => setShowDetails(false)}
        sale={sale}
      />
      {sale.customer && (
        <SendItemSideSheet
          isShown={sendSale}
          onClose={() => setSendSale(false)}
          pdfUrl={`${saleStore.apiUrl}${saleStore.apiPath}/${sale.id}/${sale.type}-pdf/`}
          filename={() =>
            `${saleTypeTitle(intl, sale.type)}-${
              sale.type === SaleType.OFFER
                ? sale.offer_number
                : sale.type === SaleType.ORDER
                ? sale.order_number
                : sale.invoice_number
            }.pdf`
          }
          title={sendSaleMessage}
          sendTitle={sendSaleMessage}
          downloadTitle={downloadSaleMessage}
          onDownload={async (language) => {
            if (sale.language !== language) {
              await saleStore.updateItem(sale, { language });
            }
            if (sale.status === SaleStatus.DRAFT) {
              await saleStore.changeState(sale, sale.type, SaleStatus.OPEN);
            }
          }}
          hasMessage={true}
          recipientCompany={sale.customer}
          onSubmit={async (values) => {
            try {
              await saleStore.sendSale(
                sale,
                values.recipients,
                values.language,
                values.addContactsCC,
                values.addResponsibleBCC,
                values.message
              );
              toaster.addToast({
                intent: 'success',
                message: intl.formatMessage({
                  defaultMessage: 'Email successfully sent to recipients',
                }),
              });
              setSendSale(false);
            } catch (error) {
              toaster.showDefaultError(error);
            }
          }}
          defaultValue={{
            recipients: (sale.customer as ICompany).default_att
              ? [(sale.customer as ICompany).default_att!]
              : (sale.customer as ICompany).email
              ? [(sale.customer as ICompany).email!]
              : undefined,
            language: sale.language || undefined,
          }}
        />
      )}
      <CreateOrderDialog
        sale={sale}
        isShown={showCreateOrder}
        onClose={() => setShowCreateOrder(false)}
      />
      <Box
        backgroundColor={
          sale.type === SaleType.OFFER
            ? defaultColors.gray[500]
            : sale.type === SaleType.ORDER
            ? defaultColors.blue[500]
            : defaultColors.green[500]
        }
        position="fixed"
        left={0}
        right={0}
        top={0}
        height={minorScale(1)}
        zIndex={98}
      />
      <Container>
        <PageHeader
          id={sale.id}
          title={
            <>
              <Box
                marginBottom={minorScale(1)}
                display="flex"
                flexShrink={0}
                width="100%"
              >
                <SaleThumbnail
                  sale={sale}
                  showNumber={false}
                  size={58}
                  marginRight={majorScale(1)}
                />
                <Box>
                  <Box display="flex" alignItems="center">
                    <Heading
                      size={300}
                      width="100%"
                      className="muted"
                      marginRight={majorScale(1)}
                    >
                      <SaleStatusTag sale={sale} />
                      <Box is="span" textTransform="capitalize">
                        <SaleTypeTitle sale={sale} />
                      </Box>{' '}
                      til{' '}
                      {sale.customer ? (
                        <Heading
                          size={300}
                          is={Link}
                          to={`/companies/${sale.customer.id}`}
                        >
                          {sale.customer.name}
                        </Heading>
                      ) : (
                        <Spinner size={16} />
                      )}
                    </Heading>
                  </Box>
                  <Heading size={600}>{sale.title}</Heading>
                </Box>
              </Box>
            </>
          }
          actions={
            <>
              <Button
                iconLeft="send-message"
                marginRight={majorScale(1)}
                onClick={() => setSendSale(true)}
              >
                {sendSaleMessage}
              </Button>

              <Popover
                placement="bottom-end"
                content={(close) => (
                  <Menu>
                    <MenuDivider text={saleTypeTitle(intl, sale.type)} />
                    <MenuItem
                      text={sendSaleMessage}
                      icon="send-message"
                      onClick={() => {
                        close();
                        setSendSale(true);
                      }}
                    />

                    {sale.type === SaleType.OFFER && (
                      <MenuItem
                        text={intl.formatMessage({
                          defaultMessage: 'Create order',
                        })}
                        icon="shopping-cart"
                        onClick={() => {
                          close();
                          createOrder();
                        }}
                      />
                    )}
                    {sale.needs_purchase_order && (
                      <MenuItem
                        text={intl.formatMessage({
                          defaultMessage: 'Reserve/purchase goods',
                        })}
                        icon="truck"
                        onClick={() => {
                          close();
                          setShowCreateOrder(true);
                        }}
                      />
                    )}

                    {/** TODO: Remove this legacy code */}
                    {sale.type === SaleType.ORDER &&
                      sale.status === SaleStatus.DRAFT && (
                        <MenuItem
                          text={intl.formatMessage({
                            defaultMessage: 'Confirm order',
                          })}
                          icon="endorsed"
                          onClick={() => {
                            close();
                            createOrder();
                          }}
                        />
                      )}
                    {/** END TODO */}

                    {sale.type === SaleType.ORDER &&
                      sale.status !== SaleStatus.VOID &&
                      sale.status !== SaleStatus.DRAFT && (
                        <MenuItem
                          text={intl.formatMessage({
                            defaultMessage: 'Create invoice',
                          })}
                          icon="dollar"
                          onClick={() => {
                            close();
                            createInvoiceAlert.show(async () => {
                              saleStore
                                .changeState(
                                  sale,
                                  SaleType.INVOICE,
                                  SaleStatus.DRAFT
                                )
                                .catch(toaster.showDefaultError);
                            });
                          }}
                        />
                      )}

                    {sale.type === SaleType.INVOICE &&
                      sale.status === SaleStatus.DRAFT && (
                        <MenuItem
                          text={intl.formatMessage({
                            defaultMessage: 'Confirm invoice',
                          })}
                          icon="confirm"
                          onClick={() => {
                            close();
                            saleStore
                              .changeState(
                                sale,
                                SaleType.INVOICE,
                                SaleStatus.OPEN
                              )
                              .then(() =>
                                confirmInvoiceSuccessAlert.show(async () =>
                                  setSendSale(true)
                                )
                              )
                              .catch(toaster.showDefaultError);
                          }}
                        />
                      )}

                    <MenuDivider text="Handlinger" />
                    <MenuItem
                      text={intl.formatMessage({
                        defaultMessage: 'Edit details',
                      })}
                      icon="edit"
                      onClick={() => {
                        close();
                        setShowDetails(true);
                      }}
                    />
                    <MenuItem
                      text={intl.formatMessage({ defaultMessage: 'Duplicate' })}
                      icon="duplicate"
                      onClick={() => {
                        close();
                        const toastId = toaster.addToast({
                          message: intl.formatMessage({
                            defaultMessage: 'Duplicating sale',
                            description: 'Toast alert when duplicating sale',
                          }),
                          intent: 'info',
                        });
                        saleStore
                          .duplicate(sale)
                          .then((duplicate) => {
                            toaster.removeToast(toastId);
                            toaster.addToast({
                              message: intl.formatMessage({
                                defaultMessage:
                                  'Sale was successfully duplicated',
                              }),
                              intent: 'success',
                            });
                            history.push(`/sales/${duplicate.id}`);
                          })
                          .catch((error) => {
                            toaster.removeToast(toastId);
                            toaster.showDefaultError(error);
                          });
                      }}
                    />

                    {sale.canDelete && (
                      <>
                        <MenuDivider />
                        <MenuItem
                          text={intl.formatMessage({
                            defaultMessage: 'Delete sale',
                          })}
                          intent="danger"
                          icon="trash"
                          onClick={async () => {
                            close();
                            deleteAlert.show(async () => {
                              try {
                                await saleStore.deleteItem(sale);
                                history.push(`/${sale.type}s`);
                                toaster.addToast({
                                  intent: 'info',
                                  message: intl.formatMessage({
                                    defaultMessage: 'Sale has been deleted.',
                                  }),
                                });
                              } catch (err) {
                                toaster.addToast({
                                  intent: 'danger',
                                  message: intl.formatMessage({
                                    defaultMessage:
                                      'Could not delete sale. Please try again.',
                                  }),
                                });
                              }
                            });
                          }}
                        />
                      </>
                    )}
                  </Menu>
                )}
              >
                <Button iconRight="chevron-down">
                  <FormattedMessage defaultMessage="Actions" />
                </Button>
              </Popover>
            </>
          }
        />

        <Section
          title={intl.formatMessage({
            defaultMessage: 'Details',
          })}
          actions={
            <Button
              iconRight="edit"
              minimal={true}
              onClick={() => setShowDetails(true)}
            >
              <FormattedMessage defaultMessage="Edit" />
            </Button>
          }
        >
          <Box display="flex">
            <Box width="50%">
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Customer',
                })}
                value={
                  sale.customer ? (
                    <CompanyThumbnail company={sale.customer} />
                  ) : (
                    <Spinner size={16} />
                  )
                }
              />
              <KeyValue keyName="Titel" value={sale.title} />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Contact persons',
                })}
                value={
                  sale.contacts.length > 0 ? (
                    <>
                      {sale.contacts.reduce((p, n) => p && !!n, true) ? (
                        sale.contacts.map((contact) => (
                          <ContactThumbnail
                            key={contact.id}
                            marginBottom={majorScale(1)}
                            marginRight={majorScale(1)}
                            contact={contact}
                          />
                        ))
                      ) : (
                        <Spinner size={16} />
                      )}
                    </>
                  ) : undefined
                }
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Currency',
                })}
                value={sale.currency}
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Invoice address',
                })}
                value={
                  sale.invoice_address
                    ? sale.invoice_address.description
                    : sale.customer && (sale.customer as ICompany).address
                    ? (sale.customer as ICompany).address!.description
                    : undefined
                }
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Delivery address',
                })}
                placeholder={intl.formatMessage({
                  defaultMessage: 'Same as invoice address',
                })}
                value={
                  sale.delivery_address
                    ? sale.delivery_address.description
                    : undefined
                }
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Delivery',
                })}
                value={formatDeliveryType(intl, sale.delivery_type)}
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Payment terms',
                })}
                value={
                  sale.payment_terms_type === PaymentTermsType.CUSTOM &&
                  sale.custom_payment_terms ? (
                    sale.custom_payment_terms
                  ) : (
                    <FormattedTerms
                      termsType={sale.payment_terms_type}
                      daysOfCredit={sale.days_of_credit}
                    />
                  )
                }
              />
            </Box>
            <Box
              width="50%"
              paddingLeft="16px"
              borderLeft={`2px solid ${defaultColors.gray[100]}`}
              alignSelf="flex-start"
            >
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Creation date',
                })}
                value={
                  <FormattedDate format="default" value={sale.created_at} />
                }
                mono
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Offer number',
                })}
                placeholder={`(${
                  authStore.currentOrganisation!.next_offer_number
                })`}
                value={
                  sale.offer_number && (
                    <>
                      {sale.offer_number}{' '}
                      {sale.offer_date && (
                        <span className="muted normal font-sans">
                          (
                          <FormattedDate
                            format="default"
                            value={sale.offer_date}
                          />
                          )
                        </span>
                      )}
                    </>
                  )
                }
                mono
              />
              {sale.type !== SaleType.OFFER && (
                <KeyValue
                  keyName={intl.formatMessage({
                    defaultMessage: 'Order number',
                  })}
                  placeholder={`(${
                    authStore.currentOrganisation!.next_order_number
                  })`}
                  value={
                    sale.order_number && (
                      <>
                        {sale.order_number}{' '}
                        {sale.order_date && (
                          <span className="muted normal font-sans">
                            (
                            <FormattedDate
                              format="default"
                              value={sale.order_date}
                            />
                            )
                          </span>
                        )}
                      </>
                    )
                  }
                  mono
                />
              )}
              {sale.type === SaleType.INVOICE && (
                <KeyValue
                  keyName={intl.formatMessage({
                    defaultMessage: 'Invoice number',
                  })}
                  placeholder={`(${
                    authStore.currentOrganisation!.next_invoice_number
                  })`}
                  value={
                    sale.invoice_number && (
                      <>
                        {sale.invoice_number}{' '}
                        {sale.invoice_date && (
                          <span className="muted normal font-sans">
                            (
                            <FormattedDate
                              format="default"
                              value={sale.invoice_date}
                            />
                            )
                          </span>
                        )}
                      </>
                    )
                  }
                  mono
                />
              )}
              {sale.type === SaleType.INVOICE && (
                <KeyValue
                  keyName={intl.formatMessage({
                    defaultMessage: 'Due date',
                  })}
                  value={
                    sale.due_date ? (
                      <FormattedDate format="default" value={sale.due_date} />
                    ) : undefined
                  }
                  mono
                />
              )}
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Reference',
                })}
                value={sale.reference}
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Description',
                })}
                value={sale.description}
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Responsible',
                })}
                value={
                  sale.responsible.length > 0 ? (
                    sale.responsible.reduce((p, n) => p && !!n, true) ? (
                      sale.responsible.map((member, i) => (
                        <UserThumbnail
                          user={member!.user}
                          key={member!.id}
                          showName
                          marginRight={majorScale(2)}
                        />
                      ))
                    ) : (
                      <Spinner size={16} />
                    )
                  ) : undefined
                }
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Tags',
                })}
                value={
                  sale.tags.length > 0
                    ? sale.tags.map((t) => (
                        <Tag
                          key={t}
                          small={true}
                          marginRight={minorScale(1)}
                          intent="info"
                        >
                          {t}
                        </Tag>
                      ))
                    : undefined
                }
              />
            </Box>
          </Box>
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage: 'Lines',
          })}
        >
          <SaleItemsTable sale={sale} showTotals />
        </Section>

        {sale.type === SaleType.INVOICE && sale.status !== SaleStatus.DRAFT && (
          <CreditNotesSection sale={sale} />
        )}

        <Section title={intl.formatMessage({ defaultMessage: 'Timeline' })}>
          <TimeLine
            timelineBaseUrl={`${saleStore.apiPath}/${sale.id}/timeline/`}
          />
        </Section>
      </Container>
    </PageContainer>
  );
});
